"use client";

import { useEffect, useState, useRef } from "react";
import { MAIL_TO_EMAIL } from "@/utils/constants";
import styles from "./style.module.scss";
import Image from "next/image";
import gsap from "gsap";

const Preloader = () => {
	const [progress, setProgress] = useState(0);
	const [isExiting, setIsExiting] = useState(false);
	const [isHidden, setIsHidden] = useState(false);
	const progressRef = useRef({ value: 0 });
	const tweenRef = useRef<gsap.core.Tween | null>(null);
	const preloaderRef = useRef<HTMLElement>(null);

	useEffect(() => {
		// Only run in browser environment
		if (typeof window === 'undefined') return;

		// Prevent scroll during preloader
		document.body.style.overflow = "hidden";
		document.body.style.pointerEvents = "none";

		let fontsLoaded = false;
		let imagesLoaded = false;
		let documentReady = false;
		let hasExited = false;

		const animateProgress = (targetProgress: number, duration: number = 0.5) => {
			// Kill any existing tween
			if (tweenRef.current) {
				tweenRef.current.kill();
			}

			// Add small random variation to make it feel more natural
			const randomVariation = Math.random() * 3;
			const adjustedTarget = Math.min(targetProgress + randomVariation, 100);

			// Animate the progress smoothly using GSAP
			tweenRef.current = gsap.to(progressRef.current, {
				value: adjustedTarget,
				duration: duration,
				ease: "power1.out",
				onUpdate: () => {
					setProgress(Math.round(progressRef.current.value));
				},
			});
		};

		// Randomized incremental progress to feel more natural
		const simulateProgress = () => {
			let currentProgress = 0;
			const progressInterval = setInterval(() => {
				if (hasExited) {
					clearInterval(progressInterval);
					return;
				}

				// Random increment between 1-5%
				const increment = Math.random() * 4 + 1;
				currentProgress = Math.min(currentProgress + increment, 95); // Cap at 95 until everything loads

				// Random delay between increments to feel more natural
				const delay = Math.random() * 150 + 50;

				animateProgress(currentProgress, delay / 1000);

				if (currentProgress >= 95) {
					clearInterval(progressInterval);
				}
			}, Math.random() * 100 + 80); // Random interval between 80-180ms

			return progressInterval;
		};

		const progressInterval = simulateProgress();

		const completeLoading = () => {
			if (hasExited) return;
			hasExited = true;

			clearInterval(progressInterval);

			// Animate to 100%
			animateProgress(100, 0.6);

			// Wait a moment at 100% before starting exit animation
			setTimeout(() => {
				if (!preloaderRef.current) return;

				setIsExiting(true);

				// Re-enable scroll and interactions
				document.body.style.overflow = "";
				document.body.style.pointerEvents = "";

				// Dispatch event to start page reveal
				window.dispatchEvent(new CustomEvent("preloader-exiting"));

				// Animate preloader clip-path from bottom to top (exit)
				gsap.to(preloaderRef.current, {
					clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)",
					duration: 1.2,
					ease: "power2.inOut",
					onComplete: () => {
						setIsHidden(true);
					}
				});
			}, 600);
		};

		const updateProgress = () => {
			if (fontsLoaded && imagesLoaded && documentReady) {
				completeLoading();
			}
		};

		// Track font loading
		if (document.fonts) {
			document.fonts.ready.then(() => {
				fontsLoaded = true;
				updateProgress();
			});
		} else {
			fontsLoaded = true;
			updateProgress();
		}

		// Track image loading
		const images = Array.from(document.images);
		let loadedImages = 0;
		const totalImages = images.length || 1;

		if (totalImages === 0) {
			imagesLoaded = true;
			updateProgress();
		} else {
			images.forEach((img) => {
				if (img.complete) {
					loadedImages++;
				} else {
					img.addEventListener("load", () => {
						loadedImages++;
						if (loadedImages >= totalImages) {
							imagesLoaded = true;
							updateProgress();
						}
					});
					img.addEventListener("error", () => {
						loadedImages++;
						if (loadedImages >= totalImages) {
							imagesLoaded = true;
							updateProgress();
						}
					});
				}
			});

			if (loadedImages >= totalImages) {
				imagesLoaded = true;
				updateProgress();
			}
		}

		// Track document ready state
		if (document.readyState === "complete") {
			documentReady = true;
			updateProgress();
		} else {
			window.addEventListener("load", () => {
				documentReady = true;
				updateProgress();
			});
		}

		// Fallback timeout to ensure preloader doesn't hang
		const timeout = setTimeout(() => {
			completeLoading();
		}, 8000);

		return () => {
			clearTimeout(timeout);
			clearInterval(progressInterval);
			// Re-enable scroll and interactions on cleanup
			document.body.style.overflow = "";
			document.body.style.pointerEvents = "";
			if (tweenRef.current) {
				tweenRef.current.kill();
			}
		};
	}, []);

	if (isHidden) return null;

	return (
		<section ref={preloaderRef} className={`${styles.preloader} ${isExiting ? styles.exit : ""}`}>
			
			<div className={styles.preloader__loader}>
				<div className={styles["loader__wrapper"]}>
					<Image src={"/images/stamp-preloader.svg"} alt="Loading..." width={724} height={316} priority data-no-placeholder />
					<div className={styles.loader}>{String(progress).padStart(2, "0")}%</div>
				</div>
			</div>
		</section>
	);
};

export default Preloader;
